import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useLang, useSettings } from "../lib/providers";
import { Button } from "./ui/button";

const STATUS_LABELS = {
  new: { fr: "Reçue", ar: "مستلمة", color: "bg-white/20", step: 1 },
  confirmed: { fr: "Confirmée", ar: "مؤكدة", color: "bg-blue-500", step: 2 },
  preparing: { fr: "En préparation", ar: "قيد التحضير", color: "bg-yellow-500", step: 3 },
  shipped: { fr: "Expédiée", ar: "مشحونة", color: "bg-orange-500", step: 4 },
  delivered: { fr: "Livrée", ar: "تم التسليم", color: "bg-green-600", step: 5 },
  cancelled: { fr: "Annulée", ar: "ملغاة", color: "bg-red-700", step: 0 },
  returned: { fr: "Retour", ar: "مرتجعة", color: "bg-red-500", step: 0 },
  postponed: { fr: "Reportée", ar: "مؤجلة", color: "bg-amber-700", step: 0 },
};

const STEPS = ["new", "confirmed", "preparing", "shipped", "delivered"];

// Résout l'identifiant technique du transporteur (ex: "kazi_tour") vers son nom
// d'affichage configuré dans settings.carriers[] (ex: "NAVEX Delivery"). Fallback
// propre (identifiant existant en majuscules) si aucun mapping n'est trouvé.
function resolveCarrierName(rawCarrier, settings) {
  const raw = rawCarrier || "";
  const match = (settings?.carriers || []).find((c) => c.id === raw || c.name === raw);
  return match?.name || raw.toUpperCase();
}

export default function TrackingPage() {
  const { id } = useParams();
  const { lang, setLang } = useLang();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("lang");
    if (p === "ar" || p === "fr") setLang(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/track/${encodeURIComponent(id)}`)
      .then(({ data }) => { if (!cancelled) { setOrder(data); setError(""); } })
      .catch(() => { if (!cancelled) setError(lang === "ar" ? "الطلبية غير موجودة" : "Commande introuvable"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, lang]);

  const t = (fr, ar) => (lang === "ar" ? ar : fr);

  return (
    <div className="min-h-screen bg-[var(--c-bg)] text-white">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-heading font-black uppercase tracking-tight text-xl text-neon" data-testid="track-home-link">
          DZ Tennis Shopper
        </Link>
        <span className="text-xs uppercase tracking-widest text-white/50">{t("Suivi commande", "تتبع الطلبية")}</span>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10" data-testid="tracking-page">
        {loading && <p className="text-white/60 text-sm">{t("Chargement…", "تحميل…")}</p>}
        {error && (
          <div className="border border-clay/40 bg-clay/10 p-6 text-center">
            <p className="font-heading text-2xl mb-3">{error}</p>
            <p className="text-white/60 text-sm mb-4">{t("Vérifiez le numéro de commande sur votre étiquette.", "تحقق من رقم الطلبية على الملصق.")}</p>
            <Link to="/"><Button className="bg-neon text-black hover:opacity-90 rounded-none font-bold uppercase tracking-widest">{t("Retour à l'accueil", "العودة للرئيسية")}</Button></Link>
          </div>
        )}
        {order && (
          <>
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-widest text-white/50">{t("Commande", "طلبية")}</p>
              <h1 className="font-heading text-3xl font-black mt-1" data-testid="tracking-order-id">{order.id}</h1>
              <p className="text-xs text-white/40 mt-1">{(order.created_at || "").slice(0, 10)}</p>
            </div>

            {/* Status pill */}
            {(() => {
              const meta = STATUS_LABELS[order.status] || STATUS_LABELS.new;
              return (
                <div className={`inline-block px-4 py-2 ${meta.color} text-white font-bold uppercase tracking-widest text-xs mb-8`} data-testid="tracking-status">
                  {t(meta.fr, meta.ar)}
                </div>
              );
            })()}

            {/* Stepper */}
            <div className="flex items-center justify-between mb-10">
              {STEPS.map((s, i) => {
                const cur = STEPS.indexOf(order.status);
                const active = cur >= 0 && i <= cur;
                return (
                  <div key={s} className="flex-1 flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-neon text-black" : "bg-white/10 text-white/40"}`}>
                      {i + 1}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-[2px] mx-1 ${active && cur > i ? "bg-neon" : "bg-white/10"}`} />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <Info label={t("Destinataire", "المستلم")} value={`${order.customer.name_masked} · ${order.customer.phone_masked}`} />
              <Info label={t("Wilaya", "الولاية")} value={order.customer.wilaya || "-"} />
              <Info label={t("Transporteur", "الناقل")} value={resolveCarrierName(order.tracking_carrier || order.delivery_carrier, settings)} />
              <Info label={t("Type", "النوع")} value={order.delivery_type === "home" ? t("À domicile", "للمنزل") : "Stop desk"} />
              <Info label={t("Articles", "المنتجات")} value={order.n_items} />
              <Info label={t("Montant", "المبلغ")} value={`${(order.total_dzd || 0).toLocaleString("fr-FR")} DZD`} />
            </div>

            {order.tracking_number && (
              <div className="border border-neon/40 bg-neon/5 p-4 mb-6">
                <p className="text-[10px] uppercase tracking-widest text-neon mb-1">{t("Numéro de suivi transporteur", "رقم تتبع الناقل")}</p>
                <p className="font-mono font-bold">{order.tracking_number}</p>
                {order.tracking_url && (
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-neon underline text-xs mt-2 inline-block">
                    {t("Suivre chez le transporteur →", "تتبع لدى الناقل ←")}
                  </a>
                )}
              </div>
            )}

            <div className="border-t border-white/10 pt-6 text-center">
              <p className="text-xs text-white/50 mb-3">{t("Une question sur votre commande ?", "هل لديك سؤال حول الطلبية ؟")}</p>
              <a href={`https://wa.me/213661976005?text=Salam, Bonjour, j'ai une question sur la commande ${order.id}`} target="_blank" rel="noreferrer">
                <Button className="bg-[#25D366] hover:opacity-90 text-white rounded-none font-bold uppercase tracking-widest" data-testid="tracking-wa-btn">
                  💬 WhatsApp
                </Button>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-white/10 p-3">
      <p className="text-[10px] uppercase tracking-widest text-white/50">{label}</p>
      <p className="text-sm font-bold mt-1">{value}</p>
    </div>
  );
}
